import { c as _c } from "react/compiler-runtime";
import { type CouponIndex, indexToOutcomeString, type Outcome } from '@svenskaspelab/poolbetting/schema/coupon';
import { BetPrediction } from '@svenskaspelab/ui/react/bet-prediction';
import type { ReactNode } from 'react';

/**
 * Props for the BetButtonsColumn component.
 * Contains the selected betting outcomes and coupon position information.
 */
type BetButtonsColumnProps = {
  /** Three-dimensional array of selected betting outcomes */
  selectedOutcomes: Outcome[][][];
  /** Zero-based index of the coupon */
  couponIndex: CouponIndex;
};
const classNames = ['justify-center', 'items-center', 'pt-[6px]', 'pb-[6px]', 'pl-[12px]', 'pr-[12px]', 'lg:pl-0', 'lg:pr-0', 'row-span-9', 'grid', 'grid-rows-subgrid', 'w-8'];

/**
 * Renders an individual bet outcome or placeholder in the column.
 *
 * @param params - The parameter object
 * @param params.outcome - The betting outcome to display (undefined for no selection)
 * @param params.index - Position index within the outcomes array
 * @param params.couponIndex - Zero-based index of the coupon for key generation
 * @returns JSX element showing either a bet prediction button or placeholder dash
 */
const getOutcome = ({
  outcome,
  index,
  couponIndex
}: {
  outcome: Outcome | undefined;
  index: number;
  couponIndex: CouponIndex;
}): ReactNode => {
  if (outcome === undefined || indexToOutcomeString[outcome] === undefined) {
    return <div className="flex justify-center" key={`overview_bet_buttons_${couponIndex}_${index}_not_selected`}>
        -
      </div>;
  }
  return <div className="flex justify-center" key={`overview_bet_buttons_${couponIndex}_${index}_${outcome}`}>
      <BetPrediction value={indexToOutcomeString[outcome]} state="selected" size="sm" />
    </div>;
};

/**
 * Component that displays bet prediction buttons in a grid column layout for coupon overview.
 * Shows the coupon index number at the top followed by selected bet outcomes.
 *
 * @param props - The component props
 * @param props.selectedOutcomes - Three-dimensional array of selected betting outcomes
 * @param props.couponIndex - Zero-based index of the coupon
 * @returns JSX element containing the bet buttons column with coupon number and outcomes
 */
export default function BetButtonsColumn(t0) {
  const $ = _c(11);
  const {
    selectedOutcomes,
    couponIndex
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classNames.join(" ");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = couponIndex + 1;
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="layer-fg-muted flex justify-center text-sm">{t2}</div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== couponIndex || $[4] !== selectedOutcomes) {
    let t5;
    if ($[6] !== couponIndex) {
      t5 = (outcomes, index) => getOutcome({
        outcome: outcomes[0]?.[0],
        index,
        couponIndex
      });
      $[6] = couponIndex;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = selectedOutcomes.map(t5);
    $[3] = couponIndex;
    $[4] = selectedOutcomes;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className={t1}>{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}